import React, { useEffect, useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import api from "../../../config/axios";
import { logger } from "../../../utils/logger";
import { usePermissions } from "../../auth/hooks/usePermissions";
import { ArcaEstado, Cliente, ClienteArca } from "../types/Client";

/** Los resultados de ARCA que alguien tiene que mirar. */
export const ARCA_A_REVISAR: ArcaEstado[] = ["sugerencia", "no_coincide", "no_encontrado"];

export const necesitaRevisionArca = (cliente?: ClienteArca | null): boolean =>
  !!cliente?.arca_estado && ARCA_A_REVISAR.includes(cliente.arca_estado);

const AVISOS: Partial<Record<ArcaEstado, string>> = {
  no_coincide: "ARCA no confirma este DNI: figura a nombre de otra persona. Revisá el documento.",
  no_encontrado: "ARCA no tiene registrado este DNI. Revisá el documento.",
};

const MOTIVOS: Partial<Record<ArcaEstado, string>> = {
  sugerencia: "Otro nombre en ARCA",
  no_coincide: "DNI de otra persona",
  no_encontrado: "DNI inexistente",
};

/** Por qué hay que revisar el cliente, en pocas palabras (para una etiqueta). */
export const motivoRevisionArca = (cliente: ClienteArca): string =>
  (cliente.arca_estado && MOTIVOS[cliente.arca_estado]) || "";

/** Lo que dijo ARCA, completo. */
export const explicacionRevisionArca = (cliente: ClienteArca): string => {
  const sugerencia = cliente.arca_estado === "sugerencia" ? cliente.arca_sugerencia : null;
  if (sugerencia) return `ARCA tiene este DNI a nombre de «${sugerencia.nombre} ${sugerencia.apellido}».`;
  return (cliente.arca_estado && AVISOS[cliente.arca_estado]) || "";
};

interface ArcaAvisoClienteProps {
  cliente: ClienteArca;
  /** El cliente como quedó después de resolver el aviso. */
  onResuelto?: (cliente: Cliente) => void;
}

/**
 * Lo que dijo ARCA del documento, cuando hay algo para revisar. Quien puede
 * editar clientes lo resuelve acá: usar el nombre de ARCA o dejarlo como está
 * (un extranjero, un nombre de uso). Las dos cosas quedan en el historial.
 */
const ArcaAvisoCliente: React.FC<ArcaAvisoClienteProps> = ({ cliente, onResuelto }) => {
  const { isAdmin } = usePermissions();
  const [estado, setEstado] = useState(cliente.arca_estado);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEstado(cliente.arca_estado);
    setError(null);
  }, [cliente.id, cliente.arca_estado]);

  if (!necesitaRevisionArca({ ...cliente, arca_estado: estado })) return null;

  const resolver = async (accion: "aplicar" | "revisado") => {
    setGuardando(true);
    setError(null);
    try {
      const { data } = await api.post(`/clientes/${cliente.id}/arca/${accion}`);
      setEstado(data.cliente.arca_estado);
      onResuelto?.(data.cliente as Cliente);
    } catch (err: unknown) {
      logger.error("Error resolviendo el aviso de ARCA:", err);
      const mensaje = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(mensaje || "No se pudo guardar. Probá de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  const sugerencia = estado === "sugerencia" ? cliente.arca_sugerencia : null;
  const texto = explicacionRevisionArca({ ...cliente, arca_estado: estado });

  return (
    <Alert
      severity={sugerencia ? "info" : "warning"}
      sx={{ py: 0.25 }}
      action={
        isAdmin ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {sugerencia && (
              <Button size="small" color="inherit" disabled={guardando} onClick={() => resolver("aplicar")}>
                Usar este nombre
              </Button>
            )}
            <Button size="small" color="inherit" disabled={guardando} onClick={() => resolver("revisado")}>
              {sugerencia ? "Dejar como está" : "El DNI es correcto"}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {texto}
      {error && <div>{error}</div>}
    </Alert>
  );
};

export default ArcaAvisoCliente;
